import { useState, Fragment, useEffect, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { uploadData } from 'aws-amplify/storage'
import { v4 as uuidv4 } from 'uuid'
import DOMPurify from 'dompurify'
import Dropdown from '../../common/Dropdown'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ToggleSwitch from '../../common/ToggleSwitch'
import { toast } from '../../common/Toast'
import CheckboxInput from '../../common/CheckboxInput'
import SimpleTextInput from '../../common/SimpleTextInput'
import RichTextInput from '../../common/RichTextInput'
import TooltipedIcon from '../../common/TooltipedIcon'
import { InformationIcon, TrashCanFull, UploadCloudFull } from '../../../icons'
import { getFileIcon } from '../../../utils/functions'
import { languagesShort } from '../../../utils/languages'
import { firmwareUploadWritePermission } from '../../../utils/permissionValidation'
import { createRelease, updateRelease } from '../../../actions/firmware'
import { getRevisionNumberValidation } from '../../../utils/requests/firmwareAPI'
import '../uploads.scss'

const AddEditReleaseModal = (props) => {
  const { t } = useTranslation()
  const { selectedRelease, closeModal, showSpinner, setShowSpinner } = props
  const [productTypes, setProductTypes] = useState([])
  const [selectedProductType, setSelectedProductType] = useState(null)
  const [modelOptions, setModelOptions] = useState([])
  const [releaseName, setReleaseName] = useState('')
  const [description, setDescription] = useState('')
  const [hasTechAccess, setHasTechAccess] = useState(false)
  const [hasUserAccess, setHasUserAccess] = useState(false)
  const [userNotes, setUserNotes] = useState('')
  const [techNotes, setTechNotes] = useState('')
  const [adminNotes, setAdminNotes] = useState('')
  const [isActive, setIsActive] = useState(false)
  const [instructionFiles, setInstructionFiles] = useState([])
  const [executableFiles, setExecutableFiles] = useState([])
  const [haveError, setHaveError] = useState({})
  const accessOptions = ['no', 'yes']
  const statuses = ['inactive', 'active']
  const instructionFirmwareType = 'Instruction'

  useLayoutEffect(() => {
    if (selectedProductType) {
      if (haveError['productType']) {
        setHaveError((prevError) => ({
          ...prevError,
          productType: false,
        }))
      }

      if (!selectedRelease) {
        const instructionFilesWithModelsReset = instructionFiles.map((file) => ({
          ...file,
          models: [],
        }))

        const executableFilesWithModelsReset = executableFiles.map((file) => ({
          ...file,
          model: null,
          firmwareType: null,
          prefix: null,
          revisionNumber: '',
        }))

        setInstructionFiles(instructionFilesWithModelsReset)
        setExecutableFiles(executableFilesWithModelsReset)
      }
    }
  }, [selectedProductType])

  useLayoutEffect(() => {
    if (selectedRelease) {
      setIsActive(selectedRelease.isActive)
      setReleaseName(selectedRelease.recordName || '')
      setDescription(selectedRelease.description || '')
      setHasTechAccess(selectedRelease.techAccess)
      setHasUserAccess(selectedRelease.userAccess)
      setUserNotes(selectedRelease.userNotes || '')
      setTechNotes(selectedRelease.techNotes || '')
      setAdminNotes(selectedRelease.adminNotes || '')

      if (selectedRelease.models && selectedRelease.models.length && props.prefixes.length) {
        const matchingPrefix = props.prefixes.find((prefix) => prefix.models.includes(selectedRelease.models[0]))
        setSelectedProductType(matchingPrefix?.productType)
      }

      const releaseInstructionFiles = selectedRelease.files?.filter((file) => file.firmwareType === instructionFirmwareType) || []
      const releaseExecutableFiles = selectedRelease.files?.filter((file) => file.firmwareType !== instructionFirmwareType) || []

      const mappedInstructionFiles = releaseInstructionFiles.map((file) => ({
        id: file.id,
        fileName: file.fileName,
        fileType: file.fileType,
        fileTitle: file.fileTitle || '',
        languageCode: file.languageCode || null,
        firmwareType: instructionFirmwareType,
        revision: file.revision || '',
        isActive: file.isActive || false,
        models: file.models || [],
      }))

      const mappedExecutableFiles = releaseExecutableFiles.map((file) => ({
        id: file.id,
        fileName: file.fileName,
        fileType: file.fileType,
        fileTitle: file.fileTitle || '',
        softwareLabel: file.softwareLabel || '',
        model: file.models?.[0] || null,
        firmwareType: file.firmwareType,
        prefix: file.prefix || null,
        revisionNumber: file.prefixNumber || '',
        isActive: file.isActive || false,
      }))

      setInstructionFiles(mappedInstructionFiles)
      setExecutableFiles(mappedExecutableFiles)
    }
  }, [selectedRelease])

  useEffect(() => {
    if (props.prefixes.length) {
      const uniqueProductTypes = [...new Set(props.prefixes.map((prefix) => prefix.productType))]
      setProductTypes(uniqueProductTypes)
    }
  }, [props.prefixes])

  useEffect(() => {
    if (selectedProductType && props.prefixes.length) {
      const filteredModels = [
        ...new Set(
          props.prefixes
            .filter((prefix) => prefix.productType === selectedProductType)
            .map((prefix) => prefix.models)
            .flat(),
        ),
      ]
      setModelOptions(filteredModels)
    }
  }, [selectedProductType])

  const firmwareHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.active_release_visible')}`} orientation="bottom" />
        <div className="label">{t('status')}</div>
        <ToggleSwitch checked={isActive} onChange={() => setIsActive((prevValue) => !prevValue)} isDisabled={showSpinner} />
        <div className="status">{t(statuses[Number(isActive)])}</div>
      </div>
    )
  }

  const firmwareFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={() => closeModal()}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton className="submit-button" onClick={handleAddEditFirmware} disabled={showSpinner} requiredPermission={firmwareUploadWritePermission}>
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!selectedProductType) {
      errors = { ...errors, productType: true }
      isValid = false
    }

    if (!releaseName.trim()) {
      errors = { ...errors, releaseName: true }
      isValid = false
    }

    instructionFiles.forEach((file) => {
      if (!file.fileTitle.trim()) {
        errors = { ...errors, [`fileTitle-${file.id}`]: true }
        isValid = false
      }

      if (!file.languageCode) {
        errors = { ...errors, [`languageCode-${file.id}`]: true }
        isValid = false
      }

      if (!file.models?.length) {
        errors = { ...errors, [`models-${file.id}`]: true }
        isValid = false
      }
    })

    executableFiles.forEach((file) => {
      if (!file.fileTitle.trim()) {
        errors = { ...errors, [`fileTitle-${file.id}`]: true }
        isValid = false
      }

      if (!file.softwareLabel.trim()) {
        errors = { ...errors, [`softwareLabel-${file.id}`]: true }
        isValid = false
      }

      if (!file.model) {
        errors = { ...errors, [`model-${file.id}`]: true }
        isValid = false
      }

      if (!file.firmwareType) {
        errors = { ...errors, [`firmwareType-${file.id}`]: true }
        isValid = false
      }

      if (!file.prefix) {
        errors = { ...errors, [`prefix-${file.id}`]: true }
        isValid = false
      }

      if (!file.revisionNumber.trim() || haveError[`revisionNumber-${file.id}`]) {
        errors = { ...errors, [`revisionNumber-${file.id}`]: true }
        isValid = false
      }
    })

    setHaveError(errors)
    return isValid
  }

  const handleMoveFilesToS3 = async () => {
    try {
      setShowSpinner(true)
      const filesToUpload = [...instructionFiles, ...executableFiles].filter((f) => f.isNew)
      for (const file of filesToUpload) {
        const key = `firmware-download-files/${file.fileName}`
        await uploadData({
          path: key,
          data: file.fileContent,
          options: {
            contentType: file.fileType,
          },
        }).result
      }
      setShowSpinner(false)
      return true
    } catch (error) {
      console.log('Error while uploading file to S3', error)
      setShowSpinner(false)
      return false
    }
  }

  const handleAddEditFirmware = async () => {
    if (executableFiles.length === 0 && instructionFiles.length === 0) {
      toast.error(t('error.must_add_one_file'))
      return
    }

    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    const mappedInstructionFiles = instructionFiles.map((file) => ({
      ...(!file.isNew && { id: file.id }),
      fileName: file.fileName,
      fileType: file.fileType,
      fileTitle: file.fileTitle,
      filePath: `firmware-download-files`,
      url: `https:///${process.env.REACT_APP_BUCKET}.s3.amazonaws.com/firmware-download-files/${file.fileName}`,
      firmwareTypeId: props.types.find((t) => t.type === file.firmwareType)?.id,
      languageCode: file.languageCode,
      revision: file.revision,
      isActive: file.isActive,
      models: file.models,
    }))

    const mappedExecutableFiles = executableFiles.map((file) => ({
      ...(!file.isNew && { id: file.id }),
      fileName: file.fileName,
      fileType: file.fileType,
      fileTitle: file.fileTitle,
      softwareLabel: file.softwareLabel,
      filePath: `firmware-download-files`,
      url: `https:///${process.env.REACT_APP_BUCKET}.s3.amazonaws.com/firmware-download-files/${file.fileName}`,
      firmwareTypeId: props.types.find((t) => t.type === file.firmwareType)?.id,
      models: [file.model],
      prefix: file.prefix,
      prefixNumber: file.revisionNumber,
      isActive: file.isActive,
    }))

    const bodyParams = {
      recordName: releaseName.trim(),
      description,
      techAccess: hasTechAccess,
      userAccess: hasUserAccess,
      userNotes: DOMPurify.sanitize(userNotes),
      adminNotes: DOMPurify.sanitize(adminNotes),
      techNotes: DOMPurify.sanitize(techNotes),
      isActive,
      files: [...mappedInstructionFiles, ...mappedExecutableFiles],
    }

    const isSuccesfull = await handleMoveFilesToS3()

    if (!isSuccesfull) {
      toast.error(t('error.something_wrong'))
      return
    }

    if (selectedRelease) {
      props.actions.updateRelease(selectedRelease.id, bodyParams).then(() => {
        closeModal()
      })
      return
    }

    props.actions.createRelease(bodyParams).then(() => {
      closeModal()
    })
  }

  const addFiles = async (event, areInstructions) => {
    if (!selectedProductType) {
      setHaveError((prevError) => ({
        ...prevError,
        productType: true,
      }))

      toast.error(t('error.must_select_product_type'))
      return
    }

    if (areInstructions) {
      const pdfFiles = Array.from(event.target.files).map((file) => ({
        id: uuidv4(),
        isNew: true,
        fileContent: file,
        fileName: file.name,
        fileType: file.type,
        fileTitle: '',
        languageCode: null,
        firmwareType: instructionFirmwareType,
        revision: '',
        isActive: false,
        models: [],
      }))
      setInstructionFiles([...instructionFiles, ...pdfFiles])
      return
    }

    const exeFiles = Array.from(event.target.files).map((file) => ({
      id: uuidv4(),
      isNew: true,
      fileContent: file,
      fileName: file.name,
      fileType: file.type,
      fileTitle: '',
      softwareLabel: '',
      model: null,
      firmwareType: null,
      prefix: null,
      revisionNumber: '',
      isActive: false,
    }))
    setExecutableFiles([...executableFiles, ...exeFiles])

    if (event.target) {
      event.target.value = ''
    }
  }

  const handleDrop = async (event, areInstructions) => {
    event.preventDefault()

    if (!selectedProductType) {
      setHaveError((prevError) => ({
        ...prevError,
        productType: true,
      }))

      toast.error(t('error.must_select_product_type'))
      return
    }

    const { files } = event.dataTransfer

    if (files && files.length) {
      if (areInstructions) {
        const pdfFiles = Array.from(event.dataTransfer.files).map((file) => ({
          id: uuidv4(),
          isNew: true,
          fileContent: file,
          fileName: file.name,
          fileType: file.type,
          fileTitle: '',
          languageCode: null,
          firmwareType: instructionFirmwareType,
          revision: '',
          isActive: false,
          models: [],
        }))
        setInstructionFiles([...instructionFiles, ...pdfFiles])
        return
      }

      const exeFiles = Array.from(event.dataTransfer.files).map((file) => ({
        id: uuidv4(),
        isNew: true,
        fileContent: file,
        fileName: file.name,
        fileType: file.type,
        fileTitle: '',
        softwareLabel: '',
        model: null,
        firmwareType: null,
        prefix: null,
        revisionNumber: '',
        isActive: false,
      }))
      setExecutableFiles([...executableFiles, ...exeFiles])
    }

    if (event.target) {
      event.target.value = ''
    }
  }

  const handleCheckValidPrefixNo = async (fileId, prefix, prefixNo) => {
    if (prefix && prefixNo.trim().length > 0) {
      setShowSpinner(true)

      try {
        const revisionValidationResponse = await getRevisionNumberValidation(prefix, prefixNo.trim())

        if (revisionValidationResponse.data) {
          setHaveError((prevError) => ({
            ...prevError,
            [`revisionNumber-${fileId}`]: true,
          }))

          return
        }
      } catch (error) {
        toast.error(t('error.something_wrong'))

        setHaveError((prevError) => ({
          ...prevError,
          [`revisionNumber-${fileId}`]: true,
        }))

        return
      } finally {
        setShowSpinner(false)
      }
    }

    setHaveError((prevError) => ({
      ...prevError,
      [`revisionNumber-${fileId}`]: false,
    }))
  }

  const updateFile = (id, updatedFields, isInstructionFile) => {
    const updatedFiles = (isInstructionFile ? instructionFiles : executableFiles).map((file) =>
      file.id === id
        ? {
            ...file,
            ...updatedFields,
            ...(!isInstructionFile && updatedFields.model && { firmwareType: null, prefix: null, revisionNumber: '' }),
            ...(!isInstructionFile && updatedFields.firmwareType && { prefix: null, revisionNumber: '' }),
            ...(!isInstructionFile && updatedFields.prefix && { revisionNumber: '' }),
          }
        : file,
    )
    isInstructionFile ? setInstructionFiles(updatedFiles) : setExecutableFiles(updatedFiles)
  }

  const getFirmwareTypeOptionsForModel = (model) => {
    if (!model) return []
    const allowedTypes = [
      ...new Set(props.prefixes.filter((p) => p.models?.includes(model) && p.firmwareType && p.firmwareType !== 'Instruction').map((p) => p.firmwareType)),
    ]
    return allowedTypes.map((type) => ({ key: type, value: type }))
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className={`uploads-modal ${selectedProductType ? '' : 'half-modal'}`}
        title={selectedRelease ? t('edit_release') : t('add_new_release')}
        headerContent={firmwareHeaderContent()}
        footerContent={firmwareFooterContent()}
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option flex-justify-start">
                {`${t('product_type')}*`}
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.select_product_type_first')}`} />
              </p>
              <Dropdown
                placeholder={t('please_select')}
                options={productTypes.map((p) => ({ key: p, value: p }))}
                initialSelectedKey={selectedProductType}
                onChange={(type) => setSelectedProductType(type.key)}
                hasError={haveError['productType']}
                isDisabled={selectedRelease}
              />
            </div>
            <div className="input-wrapper" />
          </div>
          {selectedProductType && (
            <>
              <div className="separator" />
              <div className="section-title">{t('firmware_context.new_release').toUpperCase()}</div>
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option">{`${t('firmware_context.release_name')}*`}</p>
                  <SimpleTextInput
                    value={releaseName}
                    onChange={(value) => setReleaseName(value)}
                    hasError={haveError['releaseName']}
                    onClear={() => setReleaseName('')}
                  />
                </div>
              </div>
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option">{t('description')}</p>
                  <textarea className={'textarea-input'} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
              </div>
              <div className="row-wrapper">
                <div className="input-wrapper one-third-width">
                  <p className="input-option flex-justify-start">
                    {t('tech_access')}
                    <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.only_tech')}`} />
                  </p>
                  <div className="label">
                    <ToggleSwitch checked={hasTechAccess} onChange={() => setHasTechAccess((prevValue) => !prevValue)} />
                    {t(accessOptions[Number(hasTechAccess)])}
                  </div>
                </div>
                <div className="input-wrapper">
                  <p className="input-option flex-justify-start">
                    {t('user_access')}
                    <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.only_users')}`} />
                  </p>
                  <div className="label">
                    <ToggleSwitch checked={hasUserAccess} onChange={() => setHasUserAccess((prevValue) => !prevValue)} />
                    {t(accessOptions[Number(hasUserAccess)])}
                  </div>
                </div>
              </div>
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option">{t('user_notes')}</p>
                  <RichTextInput value={userNotes} onChangeValue={(value) => setUserNotes(value)} />
                </div>
              </div>
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option">{t('tech_notes')}</p>
                  <RichTextInput value={techNotes} onChangeValue={(value) => setTechNotes(value)} />
                </div>
              </div>
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option">{t('admin_notes')}</p>
                  <RichTextInput value={adminNotes} onChangeValue={(value) => setAdminNotes(value)} />
                </div>
              </div>
              <div className="separator" />
              <div className="section-title">{t('firmware_context.instruction_files').toUpperCase()}</div>

              {instructionFiles.map((file, index) => (
                <Fragment key={index}>
                  {index !== 0 && <div className="inner-separator" />}
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <div className="label">
                        {getFileIcon(file.fileName)}
                        {file.fileName}
                        <div className="status-wrapper with-auto-margin-left">
                          <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.active_files_visible')}`} />
                          <ToggleSwitch checked={file.isActive} onChange={() => updateFile(file.id, { isActive: !file.isActive }, true)} />
                          <div className="status">{t(statuses[Number(file.isActive)])}</div>
                        </div>
                        <TrashCanFull
                          className="delete-icon without-margin-left"
                          onClick={() => setInstructionFiles(instructionFiles.filter((f, i) => i !== index))}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <p className="input-option">{`${t('file_title')}*`}</p>
                      <SimpleTextInput
                        value={file.fileTitle}
                        onChange={(value) => updateFile(file.id, { fileTitle: value }, true)}
                        onClear={() => updateFile(file.id, { fileTitle: '' }, true)}
                        hasError={haveError[`fileTitle-${file.id}`]}
                      />
                    </div>
                  </div>
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <p className="input-option">{`${t('language')}*`}</p>
                      <Dropdown
                        placeholder={t('please_select')}
                        options={props.languages.map((languageOption) => ({
                          key: languageOption,
                          value: t(languagesShort[languageOption.toLowerCase()] || languageOption),
                        }))}
                        initialSelectedKey={file.languageCode}
                        onChange={(lang) => updateFile(file.id, { languageCode: lang.key }, true)}
                        hasError={haveError[`languageCode-${file.id}`]}
                      />
                    </div>
                    <div className="input-wrapper">
                      <p className="input-option">{`${t('document_context.revision_number')}`}</p>
                      <input
                        type="text"
                        className={'name-input'}
                        value={file.revision || ''}
                        onChange={(e) => updateFile(file.id, { revision: e.target.value }, true)}
                      />
                    </div>
                  </div>
                  <div className="row-wrapper">
                    <div className="input-wrapper">
                      <p className="input-option">{`${t('monitoring.models')}*`}</p>
                      {haveError[`models-${file.id}`] && <span className="error-message">{t('error.must_select_one_model')}</span>}
                      <div className="ordered-list-wrapper">
                        <div className="columns-wrapper">
                          {modelOptions.map((model, index) => (
                            <div className="item-wrapper" key={index}>
                              <CheckboxInput
                                checked={file.models.includes(model)}
                                text={model}
                                onChange={() =>
                                  updateFile(
                                    file.id,
                                    { models: file.models.includes(model) ? file.models.filter((m) => m !== model) : [...file.models, model] },
                                    true,
                                  )
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Fragment>
              ))}
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option flex-justify-start">{t('attach_files')}</p>
                  <div className="upload-wrapper" onDrop={(e) => handleDrop(e, true)} onDragOver={(e) => e.preventDefault()}>
                    <label className="file-input-wrapper" htmlFor="file-input-instructions">
                      <UploadCloudFull />
                      <div>
                        <span>{t('other_context.drop_files_to_attach_or')}</span>
                        <span className="call-to-action">{` ${t('browse')}`}</span>
                      </div>
                    </label>
                    <input className="file-input" id="file-input-instructions" onChange={(e) => addFiles(e, true)} type="file" multiple />
                  </div>
                </div>
              </div>
              <div className="separator" />
              <div className="section-title">{t('firmware_context.firmware_files').toUpperCase()}</div>
              {executableFiles.map((file, index) => (
                <Fragment key={index}>
                  {index !== 0 && <div className="inner-separator" />}
                  <>
                    <div className="row-wrapper">
                      <div className="input-wrapper">
                        <div className="label">
                          {getFileIcon(file.fileName)}
                          {file.fileName}
                          <div className="status-wrapper with-auto-margin-left">
                            <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.active_files_visible')}`} />
                            <ToggleSwitch checked={file.isActive} onChange={() => updateFile(file.id, { isActive: !file.isActive })} />
                            <div className="status">{t(statuses[Number(file.isActive)])}</div>
                          </div>
                          <TrashCanFull
                            className="delete-icon without-margin-left"
                            onClick={() => setExecutableFiles(executableFiles.filter((f, i) => i !== index))}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row-wrapper">
                      <div className="input-wrapper">
                        <p className="input-option flex-justify-start">
                          {`${t('firmware_context.file_display_title')}*`}
                          <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.file_name_content')}`} orientation="extra-right" />
                        </p>
                        <SimpleTextInput
                          value={file.fileTitle}
                          onChange={(value) => updateFile(file.id, { fileTitle: value })}
                          onClear={() => updateFile(file.id, { fileTitle: '' })}
                          hasError={haveError[`fileTitle-${file.id}`]}
                          disabled={!file.prefix || !file.revisionNumber}
                        />
                        <div className="small-disclaimer">{t('firmware_context.displayed_to_users')}</div>
                      </div>
                      <div className="input-wrapper">
                        <p className="input-option flex-justify-start">{`${t('firmware_context.device_software_label')}*`}</p>
                        <SimpleTextInput
                          value={file.softwareLabel}
                          onChange={(value) => updateFile(file.id, { softwareLabel: value })}
                          onClear={() => updateFile(file.id, { softwareLabel: '' })}
                          hasError={haveError[`softwareLabel-${file.id}`]}
                          disabled={!file.prefix || !file.revisionNumber}
                        />
                        <div className="small-disclaimer">{t('firmware_context.actual_label')}</div>
                      </div>
                    </div>
                    <div className="row-wrapper">
                      <div className="input-wrapper">
                        <p className="input-option flex-justify-start">
                          {`${t('model')}*`}
                          <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.select_to_enable_model')}`} orientation="extra-right" />
                        </p>
                        <Dropdown
                          placeholder={t('please_select')}
                          options={modelOptions.map((m) => ({ key: m, value: m }))}
                          initialSelectedKey={file.model}
                          onChange={(model) => updateFile(file.id, { model: model.key })}
                          hasError={haveError[`model-${file.id}`]}
                        />
                      </div>
                      <div className="input-wrapper">
                        <p className="input-option flex-justify-start">
                          {`${t('firmware_context.firmware_type')}*`}
                          <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('firmware_context.select_to_enable_type')}`} />
                        </p>
                        <Dropdown
                          placeholder={t('please_select')}
                          options={getFirmwareTypeOptionsForModel(file.model)}
                          initialSelectedKey={file.firmwareType}
                          onChange={(type) => updateFile(file.id, { firmwareType: type.key })}
                          hasError={haveError[`firmwareType-${file.id}`]}
                          isDisabled={!file.model || getFirmwareTypeOptionsForModel(file.model).length === 0}
                        />
                      </div>
                    </div>
                    <div className="row-wrapper">
                      <div className="input-wrapper">
                        <p className="input-option flex-justify-start">
                          {`${t('prefix')}*`}
                          <TooltipedIcon
                            icon={<InformationIcon />}
                            tooltipText={`${t('firmware_context.select_to_enable_prefix')}`}
                            orientation="extra-right"
                          />
                        </p>
                        <Dropdown
                          placeholder={t('please_select')}
                          options={props.prefixes
                            .filter((p) => p.models.includes(file.model) && p.firmwareType === file.firmwareType)
                            .map((t) => ({ key: t.prefix, value: t.prefix }))}
                          initialSelectedKey={file.prefix}
                          onChange={(p) => updateFile(file.id, { prefix: p.key })}
                          hasError={haveError[`prefix-${file.id}`]}
                          isDisabled={!file.model || !file.firmwareType}
                        />
                      </div>
                      <div className="input-wrapper">
                        <p className="input-option">{`${t('document_context.revision_number')}*`}</p>
                        <input
                          type="text"
                          className={'name-input' + (haveError[`revisionNumber-${file.id}`] ? ' has-error' : '')}
                          value={file.revisionNumber}
                          onChange={(e) =>
                            updateFile(file.id, {
                              revisionNumber: e.target.value,
                              fileTitle: `${file.prefix}R${e.target.value}`,
                              softwareLabel: `${file.prefix}R${e.target.value}`,
                            })
                          }
                          onBlur={() => handleCheckValidPrefixNo(file.id, file.prefix, file.revisionNumber)}
                          disabled={!file.prefix}
                        />
                        {haveError[`revisionNumber-${file.id}`] && file.revisionNumber.length > 0 && (
                          <span className="error-message">{t('error.revision_number_already_used')}</span>
                        )}
                      </div>
                    </div>
                  </>
                </Fragment>
              ))}
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option flex-justify-start">{t('attach_files')}</p>
                  <div className="upload-wrapper" onDrop={(e) => handleDrop(e)} onDragOver={(e) => e.preventDefault()}>
                    <label className="file-input-wrapper" htmlFor="file-input-exe">
                      <UploadCloudFull />
                      <div>
                        <span>{t('other_context.drop_files_to_attach_or')}</span>
                        <span className="call-to-action">{` ${t('browse')}`}</span>
                      </div>
                    </label>
                    <input className="file-input" id="file-input-exe" onChange={(e) => addFiles(e)} type="file" multiple />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </PageModal>
    </>
  )
}

function stateToProps({ firmware, shared }) {
  return {
    prefixes: firmware?.prefixes || [],
    types: firmware?.types || [],
    languages: shared?.languages?.map((l) => l.languageCode) || [],
    selectedRelease: firmware?.releaseDetails,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createRelease,
        updateRelease,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditReleaseModal)

